import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { parse } from 'tldts';

//Redux
import { selectWorkflowsConfig } from 'reducers/workflows';

//Assets
import PoweredByForethoughtIcon from 'assets/icons/forethought-footer-logo.svg';
import {
  selectLegibleTextColor,
  selectOrigin,
  selectIsFooterDisabled,
  selectShowRestartButtonInFooter,
} from 'reducers/config';
import { selectFeatureFlags } from 'reducers/workflows';
import { IconReload } from '@tabler/icons-react';
import { ModalHeaderActionButton } from 'components/Modal/styles';
import { Tooltip } from '@forethought-technologies/forethought-elements';
import { Box, Typography, useTheme } from '@mui/material';
import { modalRestartConversation } from 'actions/workflows-v2/workflowsV2';

const PoweredByText = () => {
  const theme = useTheme();

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: '1px' }}>
      <img alt='' height={16} src={PoweredByForethoughtIcon} />
      <Typography
        sx={{
          '&:hover': {
            color: theme.palette.colors.grey[900],
          },

          color: theme.palette.colors.grey[700],
          fontFamily: 'Plus Jakarta Sans !important',
          fontSize: '10px',
          fontWeight: 600,
        }}
      >
        Powered by Forethought
      </Typography>
    </Box>
  );
};

const WorkflowsFooter = () => {
  const dispatch = useAppDispatch();
  const workflowsConfig = useAppSelector(selectWorkflowsConfig);
  const legibleTextColor = useAppSelector(selectLegibleTextColor);
  const origin = useAppSelector(selectOrigin);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const isFooterDisabled = useAppSelector(selectIsFooterDisabled);
  const showRestartButtonInFooter = useAppSelector(
    selectShowRestartButtonInFooter,
  );

  const isBrandingDisabled = workflowsConfig?.disable_forethought_branding;
  const isPoweredByForethoughtLinkEnabled = featureFlags.includes(
    'powered_by_forethought_link',
  );

  const parsedOrigin = parse(origin);

  // `parsedOrigin.hostname` fallback is needed for cases like
  // http://localhost:7890
  const utm_source = parsedOrigin.domainWithoutSuffix || parsedOrigin.hostname;

  if (isFooterDisabled) {
    // needs some height so widget border radius is still visible
    return <Box component='footer' sx={{ height: '10px' }} />;
  }

  return (
    <FooterContainer data-testid='widget-footer'>
      {!isBrandingDisabled && (
        <Box
          data-testid='footer-brand-icon'
          sx={{ alignItems: 'center', display: 'flex' }}
        >
          {isPoweredByForethoughtLinkEnabled ? (
            <PoweredByForethoughtLink
              href={`https://forethought.ai/?utm_medium=product-widget&utm_source=${utm_source}&utm_campaign=widget-traffic&utm_content=corporate-website`}
              target='_blank'
            >
              <PoweredByText />
            </PoweredByForethoughtLink>
          ) : (
            <PoweredByText />
          )}
        </Box>
      )}
      {showRestartButtonInFooter && (
        <RestartContainer>
          <Tooltip tooltipContent='Restart chat'>
            <ModalHeaderActionButton
              aria-label='Restart chat'
              onClick={() => {
                dispatch(modalRestartConversation());
              }}
            >
              <IconReload color={legibleTextColor} size={16} />
            </ModalHeaderActionButton>
          </Tooltip>
        </RestartContainer>
      )}
    </FooterContainer>
  );
};

export default WorkflowsFooter;

const FooterContainer = styled('footer')`
  display: flex;
  justify-content: center;
  padding: 12px;
  width: 100%;
  position: relative;
  box-sizing: border-box;
`;

const RestartContainer = styled('div')`
  position: absolute;
  left: 10px;
  bottom: 4px;
`;

const PoweredByForethoughtLink = styled('a')`
  display: block;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid var(--legible-element-color);
  }

  .hovered {
    display: none;
  }

  &:hover {
    .hovered {
      display: initial;
    }

    .not-hovered {
      display: none;
    }
  }
`;
